import { getApiBaseUrl } from '../config/api';

// FEAT-046 — Admin client for the Credential Review Queue (MVP-18.20,
// MVP-18.21). Wraps the two admin endpoints exposed by the API:
//   GET   /admin/credential-documents      → paginated queue
//   PATCH /admin/credential-documents/:id  → accept / reject (with reason)
// Accepted documents are the source of the credential badge entitlements
// consumed by FEAT-039 on the public caregiver profile.

export type CredentialDocStatus = 'PENDING_REVIEW' | 'ACCEPTED' | 'REJECTED';
export type CredentialDocType =
  | 'STATE_CERT'
  | 'TRAINING_CERT'
  | 'TRANSCRIPT'
  | 'OTHER';
export type ReviewDecision = 'ACCEPTED' | 'REJECTED';

export interface CredentialReviewDecisionRow {
  id: string;
  adminId: string;
  decision: ReviewDecision;
  reason: string | null;
  decidedAt: string;
}

export interface CredentialDocumentRow {
  id: string;
  caregiverId: string;
  title: string;
  docType: CredentialDocType;
  notes: string | null;
  storageUri: string;
  mimeType: string;
  sizeBytes: number;
  status: CredentialDocStatus;
  aiProcessingTag: Record<string, unknown> | null;
  uploadedAt: string;
  expiresAt: string | null;
  reviewDecisionId: string | null;
  reviewDecision?: CredentialReviewDecisionRow | null;
  caregiver?: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

export interface CredentialDocumentListResponse {
  documents: CredentialDocumentRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CredentialReviewQueueFilters {
  status?: CredentialDocStatus;
  caregiverId?: string;
  page?: number;
  limit?: number;
}

export interface ApplyDecisionInput {
  documentId: string;
  decision: ReviewDecision;
  reason?: string;
}

class CredentialReviewService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async listQueue(
    filters: CredentialReviewQueueFilters,
  ): Promise<CredentialDocumentListResponse> {
    const baseUrl = await getApiBaseUrl();
    const qs = new URLSearchParams();
    if (filters.status) qs.set('status', filters.status);
    if (filters.caregiverId) qs.set('caregiverId', filters.caregiverId);
    if (filters.page) qs.set('page', String(filters.page));
    if (filters.limit) qs.set('limit', String(filters.limit));
    const query = qs.toString();
    const response = await fetch(
      `${baseUrl}/admin/credential-documents${query ? `?${query}` : ''}`,
      { headers: this.getAuthHeaders() },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.message || body?.error || 'Failed to load credential review queue');
    }
    return response.json();
  }

  async applyDecision(
    input: ApplyDecisionInput,
  ): Promise<{ document: CredentialDocumentRow }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/admin/credential-documents/${encodeURIComponent(input.documentId)}`,
      {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          decision: input.decision,
          ...(input.reason ? { reason: input.reason } : {}),
        }),
      },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.message || body?.error || 'Failed to record decision');
    }
    return response.json();
  }
}

export const credentialReviewService = new CredentialReviewService();
